import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Icon } from "@/lib/icons";
import {
  GetPaidField,
  payoutContainerClass,
  payoutPageShellClass,
  payoutPrimaryActionClass,
  payoutSecondaryActionClass,
  payoutSelectTriggerClass,
} from "@/components/get-paid";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ContributePageHeading } from "@/components/contribute/ContributePageHeading";
import { PHONEME_RECORDING_LIMIT, PHRASE_RECORDING_LIMIT, TRANSLATION_TIME_LIMIT } from "./constants";
import { formatDuration } from "./helpers";
import type { useGetPaid } from "./useGetPaid";
import {
  QualificationTestSection,
  SessionMetric,
  SessionSectionNav,
  type QualificationSectionStat,
} from "./QualificationSessionChrome";

type QualificationTestWorkspaceProps = {
  ctx: ReturnType<typeof useGetPaid>;
  embedded?: boolean;
};

export function QualificationTestWorkspace({ ctx, embedded = false }: QualificationTestWorkspaceProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const autoStartKeyRef = useRef<string | null>(null);
  const {
    t,
    tPage,
    locale,
    currentTest,
    selectedTestLang,
    setSelectedTestLang,
    selectTestLanguage,
    contributedTestLanguages,
    loadingLanguages,
    testEligibility,
    selectedLanguageEligibility,
    dataPhonemes,
    dataPhrasesLocalFr,
    dataPhrasesLocalOnly,
    statuses,
    attempts,
    timers,
    audioUrls,
    translations,
    activeItemId,
    testTimeRemaining,
    isTestExpired,
    submittingUserTest,
    startingTest,
    isStartTestBlocked,
    selectedSubmission,
    payoutState,
    handleStartTest,
    handleFinalSubmit,
    startRecording,
    stopRecording,
    redoRecording,
    handleTranslationFocus,
    handleTranslationChange,
    allItemsCompleted,
  } = ctx;

  const linkedLanguage = searchParams.get("language") || "";
  const languageOptions = contributedTestLanguages.length
    ? contributedTestLanguages
    : [];

  useEffect(() => {
    if (linkedLanguage && linkedLanguage !== selectedTestLang) {
      setSelectedTestLang(linkedLanguage);
    }
  }, [linkedLanguage, selectedTestLang, setSelectedTestLang]);

  useEffect(() => {
    if (
      searchParams.get("session") !== "test" ||
      !linkedLanguage ||
      currentTest ||
      startingTest ||
      selectedTestLang !== linkedLanguage
    ) {
      if (searchParams.get("session") !== "test") {
        autoStartKeyRef.current = null;
      }
      return;
    }

    const autoStartKey = `test:${linkedLanguage}`;
    if (autoStartKeyRef.current === autoStartKey) return;
    autoStartKeyRef.current = autoStartKey;
    void handleStartTest();
  }, [currentTest, handleStartTest, linkedLanguage, searchParams, selectedTestLang, startingTest]);

  useEffect(() => {
    if (!currentTest) return;
    if (searchParams.get("session") === "test" && searchParams.get("language") === currentTest.language) return;

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("section", "test");
      next.set("session", "test");
      next.set("language", currentTest.language);
      return next;
    }, { replace: true });
  }, [currentTest, searchParams, setSearchParams]);

  const handleLanguageChange = (value: string) => {
    selectTestLanguage(value);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("section", "test");
      next.set("session", "lang");
      next.set("language", value);
      return next;
    }, { replace: true });
  };

  const handleStartTestFromLink = async () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("section", "test");
      next.set("session", "lang");
      next.set("language", selectedTestLang);
      return next;
    }, { replace: true });
    await handleStartTest();
  };

  const totalItems = dataPhonemes.length + dataPhrasesLocalFr.length + dataPhrasesLocalOnly.length;
  const completedItems = useMemo(
    () => Object.values(statuses).filter((status) => status === "completed").length,
    [statuses],
  );
  const completionPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  const sectionStats: QualificationSectionStat[] = useMemo(
    () => [
      {
        id: "test-section-phonemes",
        index: "01",
        label: t.getPaid.sectionPhonemes,
        count: dataPhonemes.length,
        completed: dataPhonemes.reduce(
          (total, _item, index) => total + (statuses[`ph-${index}`] === "completed" ? 1 : 0),
          0,
        ),
      },
      {
        id: "test-section-phrases",
        index: "02",
        label: t.getPaid.sectionPhrases,
        count: dataPhrasesLocalFr.length,
        completed: dataPhrasesLocalFr.reduce(
          (total, _item, index) => total + (statuses[`pp-${index}`] === "completed" ? 1 : 0),
          0,
        ),
      },
      {
        id: "test-section-translations",
        index: "03",
        label: t.getPaid.sectionTranslations,
        count: dataPhrasesLocalOnly.length,
        completed: dataPhrasesLocalOnly.reduce(
          (total, _item, index) => total + (statuses[`tp-${index}`] === "completed" ? 1 : 0),
          0,
        ),
      },
    ],
    [dataPhrasesLocalFr, dataPhrasesLocalOnly, dataPhonemes, statuses, t.getPaid],
  );

  if (!currentTest) {
    return (
      <div className={embedded ? "space-y-8" : payoutPageShellClass}>
        <div className={embedded ? "space-y-8" : payoutContainerClass}>
          <ContributePageHeading
            eyebrow={t.getPaid.qualificationTest}
            title={tPage.testTitle || "Validation Test"}
            description={tPage.testSubtitle || "Complete the validation test to unlock earnings"}
          />
          <section className="py-6">
            <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(16rem,0.65fr)] md:items-end">
              <div>
                <p className="text-[0.66rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground/58">
                  {tPage.testLanguageLabel}
                </p>
                <div className="mt-3 max-w-sm">
                  <Select
                    value={selectedTestLang}
                    onValueChange={handleLanguageChange}
                    disabled={loadingLanguages || languageOptions.length === 0}
                  >
                    <SelectTrigger className={payoutSelectTriggerClass} id="test-lang">
                      <SelectValue placeholder={tPage.chooseLanguage} />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingLanguages ? (
                        <div className="p-2 text-sm text-muted-foreground">{tPage.loadingLanguages}</div>
                      ) : languageOptions.length > 0 ? (
                        languageOptions.map((lang: any) => (
                          <SelectItem key={lang.id || lang.code} value={lang.code}>
                            {lang.flag && <span>{lang.flag} </span>}
                            {lang.displayName || lang.name} · {testEligibility?.languages.find((entry) => entry.language === String(lang.code).toLowerCase())?.validatedAudioCount ?? 0}/{testEligibility?.threshold ?? 50}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-muted-foreground">{tPage.noLanguagesAvailable}</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="md:border-l md:border-border/40 md:pl-6">
                {selectedSubmission ? (
                  <p className="text-sm text-muted-foreground/76">
                    {tPage.statusLabel}{" "}
                    <span className="font-semibold text-foreground">
                      {selectedSubmission.status === "PENDING"
                        ? tPage.testStatusPending
                        : selectedSubmission.status === "VALIDATED"
                          ? tPage.testStatusValidated
                          : tPage.testStatusRejected}
                    </span>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground/76">{tPage.noTestSubmitted}</p>
                )}
                <Button
                  onClick={handleStartTestFromLink}
                  disabled={isStartTestBlocked}
                  className={`mt-4 ${payoutPrimaryActionClass}`}
                >
                  <Icon name="bookOpen" className="mr-2 h-4 w-4" />
                  {startingTest ? tPage.loading : tPage.startTest}
                </Button>
              </div>
            </div>
          </section>
          {selectedLanguageEligibility ? (
            <section className="border-l-2 border-brand/70 pl-4">
              <p className="text-sm font-semibold text-foreground">
                {selectedLanguageEligibility.eligible
                  ? tPage.testLanguageEligible
                  : tPage.testLanguageProgress
                      .replace("{validated}", String(selectedLanguageEligibility.validatedAudioCount))
                      .replace("{threshold}", String(testEligibility?.threshold ?? 50))}
              </p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground/76">
                {tPage.testLanguageContributionSummary
                  .replace("{total}", String(selectedLanguageEligibility.totalAudioContributions))
                  .replace("{validated}", String(selectedLanguageEligibility.validatedAudioCount))}
              </p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground/76">
                {selectedLanguageEligibility.eligible
                  ? tPage.testLanguageEligibleDescription
                  : tPage.testLanguageRemaining
                      .replace("{remaining}", String(selectedLanguageEligibility.remaining))
                      .replace("{language}", selectedTestLang.toUpperCase())}
              </p>
              <div className="mt-4 h-1.5 max-w-xl overflow-hidden rounded-full bg-muted/60">
                <div
                  className="h-full rounded-full bg-brand transition-[width] duration-500"
                  style={{ width: `${selectedLanguageEligibility.progress}%` }}
                />
              </div>
              <p className="mt-4 text-xs leading-5 text-muted-foreground/68">
                {tPage.communityValidationRule}
              </p>
            </section>
          ) : !loadingLanguages ? (
            <section className="border-l-2 border-border pl-4">
              <p className="text-sm font-semibold text-foreground">{tPage.noTestContributionLanguage}</p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground/76">{tPage.noTestContributionLanguageDescription}</p>
            </section>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-8" : payoutPageShellClass}>
      <div className={embedded ? "space-y-8" : payoutContainerClass}>
        <header className="border-b border-border/40 pb-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <ContributePageHeading
              className="space-y-4"
              eyebrow={t.getPaid.qualificationTest}
              title={currentTest.title}
              description={tPage.testSubtitle || "Complete the validation test to unlock earnings"}
              titleClassName="text-3xl text-3xl sm:text-4xl"
              descriptionClassName="max-w-xl text-sm leading-6 sm:text-base"
            />
            <div className="flex shrink-0 flex-col items-start gap-3 lg:items-end">
              <p className={isTestExpired ? "text-sm font-semibold text-danger" : "text-sm text-muted-foreground/76"}>
                {t.getPaid.testTimeRemaining}: <span className="font-semibold text-foreground">{formatDuration(testTimeRemaining)}</span>
              </p>
              <Button
                onClick={handleFinalSubmit}
                disabled={!allItemsCompleted() || submittingUserTest || isTestExpired}
                className={payoutPrimaryActionClass}
              >
                {submittingUserTest ? (
                  <><Icon name="loader2" className="mr-2 h-4 w-4 animate-spin" />{t.getPaid.submitting}</>
                ) : (
                  <><Icon name="check" className="mr-2 h-4 w-4" />{t.getPaid.submitTest}</>
                )}
              </Button>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-3">
            <SessionMetric label={tPage.testLanguageLabel} value={selectedTestLang.toUpperCase()} />
            <SessionMetric label={tPage.itemsLabel || t.getPaid.sectionPhonemes} value={`${completedItems}/${totalItems}`} />
            <SessionMetric label={tPage.progressLabel || t.getPaid.submitEnabledWhenComplete} value={`${completionPercent}%`} tone="amber" />
          </div>
          <div className="mt-5 h-1 overflow-hidden bg-muted/50">
            <div className="h-full bg-brand transition-[width] duration-500" style={{ width: `${completionPercent}%` }} />
          </div>
        </header>

        <SessionSectionNav
          sections={sectionStats}
          completedLabel={t.completed || "Completed"}
          navigationLabel={t.getPaid.qualificationTest}
        />

        <QualificationTestSection
          id="test-section-phonemes"
          index="01"
          title={t.getPaid.sectionPhonemes}
          count={dataPhonemes.length}
          completed={sectionStats[0].completed}
          completedLabel={t.completed || "Completed"}
        >
          <div className="grid gap-x-8 md:grid-cols-2">
            {dataPhonemes.map((phoneme: any, index: number) => (
              <RecordingExercise
                key={`ph-${index}`}
                id={`ph-${index}`}
                label={phoneme}
                status={statuses[`ph-${index}`] ?? "idle"}
                attempts={attempts[`ph-${index}`] ?? 0}
                remaining={timers[`ph-${index}`] ?? PHONEME_RECORDING_LIMIT}
                audioUrl={audioUrls[`ph-${index}`]}
                active={activeItemId === `ph-${index}`}
                expired={isTestExpired}
                limit={PHONEME_RECORDING_LIMIT}
                t={t}
                onStart={() => startRecording(`ph-${index}`, PHONEME_RECORDING_LIMIT)}
                onStop={stopRecording}
                onRedo={() => redoRecording(`ph-${index}`, PHONEME_RECORDING_LIMIT)}
              />
            ))}
          </div>
        </QualificationTestSection>

        <QualificationTestSection
          id="test-section-phrases"
          index="02"
          title={t.getPaid.sectionPhrases}
          count={dataPhrasesLocalFr.length}
          completed={sectionStats[1].completed}
          completedLabel={t.completed || "Completed"}
        >
          <div>
            {dataPhrasesLocalFr.map((phrase: any, index: number) => (
              <RecordingExercise
                key={`pp-${index}`}
                id={`pp-${index}`}
                label={phrase.local}
                secondaryLabel={phrase.french}
                status={statuses[`pp-${index}`] ?? "idle"}
                attempts={attempts[`pp-${index}`] ?? 0}
                remaining={timers[`pp-${index}`] ?? PHRASE_RECORDING_LIMIT}
                audioUrl={audioUrls[`pp-${index}`]}
                active={activeItemId === `pp-${index}`}
                expired={isTestExpired}
                limit={PHRASE_RECORDING_LIMIT}
                t={t}
                onStart={() => startRecording(`pp-${index}`, PHRASE_RECORDING_LIMIT)}
                onStop={stopRecording}
                onRedo={() => redoRecording(`pp-${index}`, PHRASE_RECORDING_LIMIT)}
              />
            ))}
          </div>
        </QualificationTestSection>

        <QualificationTestSection
          id="test-section-translations"
          index="03"
          title={t.getPaid.sectionTranslations}
          count={dataPhrasesLocalOnly.length}
          completed={sectionStats[2].completed}
          completedLabel={t.completed || "Completed"}
          emptyMessage={tPage.noTranslationItems}
        >
          <div>
            {dataPhrasesLocalOnly.map((phrase: any, index: number) => {
              const id = `tp-${index}`;
              const status = statuses[id] ?? "idle";
              return (
                <div key={id} className="grid gap-4 border-b border-border/35 py-5 last:border-b-0 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_auto] md:items-start">
                  <div>
                    <ExerciseIndex index={index + 1} status={status} />
                    <p className="mt-3 text-sm font-semibold text-foreground">{phrase.local}</p>
                  </div>
                  <textarea
                    className="min-h-24 w-full resize-y border border-border/50 bg-transparent px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/45 focus:border-brand/60 focus:ring-0"
                    placeholder={t.getPaid.translationPlaceholder}
                    value={translations[id] || ""}
                    disabled={isTestExpired}
                    onFocus={() => handleTranslationFocus(id)}
                    onChange={(event) => handleTranslationChange(id, event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground/66 md:pt-2">
                    {t.getPaid.time}: {timers[id] !== undefined ? timers[id] || 0 : TRANSLATION_TIME_LIMIT}s
                  </p>
                </div>
              );
            })}
          </div>
        </QualificationTestSection>

        <footer className="border-t border-border/40 pt-6">
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground/70">{t.getPaid.submitEnabledWhenComplete}</p>
        </footer>
      </div>
    </div>
  );
}

function ExerciseIndex({ index, status }: { index: number; status: string }) {
  const tone = status === "completed" ? "text-success" : status === "in_progress" ? "text-brand" : "text-muted-foreground/55";
  return <span className={`font-mono text-xs ${tone}`}>{String(index).padStart(2, "0")}</span>;
}

function RecordingExercise({
  id,
  label,
  secondaryLabel,
  status,
  attempts,
  remaining,
  audioUrl,
  active,
  expired,
  limit,
  t,
  onStart,
  onStop,
  onRedo,
}: {
  id: string;
  label: string;
  secondaryLabel?: string;
  status: string;
  attempts: number;
  remaining: number;
  audioUrl?: string;
  active: boolean;
  expired: boolean;
  limit: number;
  t: any;
  onStart: () => void;
  onStop: () => void;
  onRedo: () => void;
}) {
  return (
    <div className="border-b border-border/35 py-5 last:border-b-0">
      <div className="flex items-start gap-4">
        <ExerciseIndex index={Number(id.split("-")[1]) + 1} status={status} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
            <div>
              <p className="text-sm font-semibold text-foreground">{label}</p>
              {secondaryLabel ? <p className="mt-1 text-sm text-muted-foreground/66">{secondaryLabel}</p> : null}
            </div>
            <p className="text-xs text-muted-foreground/58">{t.getPaid.attempt} {Math.min(attempts + 1, 2)}/2</p>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              className={payoutSecondaryActionClass}
              onClick={onStart}
              disabled={attempts >= 2 || active || expired}
            >
              <Icon name="mic" className="mr-2 h-3.5 w-3.5" />
              {active ? `${t.getPaid.recording}... ${remaining || 0}s` : `${t.record} (${limit}s)`}
            </Button>
            {active ? <Button size="sm" variant="ghost" className="h-10 rounded-full px-3 text-xs" onClick={onStop}>{t.getPaid.stop}</Button> : null}
          </div>
          {audioUrl ? (
            <div className="mt-4 flex flex-col gap-3 border-t border-border/30 pt-3 sm:flex-row sm:items-center">
              <audio controls src={audioUrl} className="h-8 min-w-0 flex-1" />
              <Button size="sm" variant="ghost" className="h-8 rounded-full px-3 text-xs" onClick={onRedo} disabled={attempts >= 2 || expired}>
                {t.getPaid.redo}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
